import { useCallback, useMemo, useState } from 'react';
import ky, { HTTPError } from 'ky';
import { Link, useHistory } from 'react-router-dom';
import { IsNotEmpty, IsString, MinLength } from 'class-validator';
import { useTranslation } from 'react-i18next';
import Form from 'src/components/FormComponents/Form';
import Input from 'src/components/FormComponents/Input';
import PasswordInput from 'src/components/FormComponents/PasswordInput';
import { Button } from 'src/components/Buttons/Button';
import { LandingPageHeader } from 'src/view/screens/Mundu/components';
import { Screen } from 'src/view/routes/Router';
import { SERVER_URL } from 'src/config';
import i18n from 'src/i18n';
import { useNotification } from 'src/components/Notification/useNotfication';
import { ActivateAccountState } from './ActivateAccount';

class RequestCodeSchema {
    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    identifier: string;
}

class CompleteResetSchema {
    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    code: string;

    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @MinLength(8, { message: () => i18n.t('validation.passwordTooShort') })
    newPassword: string;

    @IsString({ message: () => i18n.t('validation.requiredField') })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    confirmPassword: string;
}

type ResetRequestPayload = { email: string } | { phoneNumber: string };

export const ResetPassword = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const notify = useNotification();
    const [codeSentTo, setCodeSentTo] = useState<string>();
    const [resetRequest, setResetRequest] = useState<
        ResetRequestPayload | undefined
    >();
    const [isRequesting, setIsRequesting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    const headerColors = useMemo(
        () => ({
            textColor: 'woodyBrown',
            activeTextColor: 'mongooseGray',
            backgroundColor: 'lightPeach',
        }),
        [],
    );

    const handleRequestCode = useCallback(
        async (values: RequestCodeSchema) => {
            const rawIdentifier = values.identifier.trim();
            const useEmail = rawIdentifier.includes('@');
            const phoneInput = rawIdentifier.replace(/\s/g, '');
            const usePhoneNumber = /^\+?\d+$/.test(phoneInput);

            if (!useEmail && !usePhoneNumber) {
                notify(t('resetPassword.request.genericError'), true);
                return;
            }

            const payload: ResetRequestPayload = useEmail
                ? { email: rawIdentifier.toLowerCase() }
                : {
                      phoneNumber: phoneInput.startsWith('+')
                          ? phoneInput
                          : `+47${phoneInput}`,
                  };

            const displayTarget =
                'email' in payload ? payload.email : payload.phoneNumber;

            setIsRequesting(true);
            setCodeSentTo(undefined);
            setResetRequest(undefined);

            try {
                await ky.post('auth/forgot-password', {
                    prefixUrl: SERVER_URL,
                    json: payload,
                });
                setCodeSentTo(displayTarget);
                setResetRequest(payload);
                notify(
                    t('resetPassword.request.success', {
                        email: displayTarget,
                    }),
                );
            } catch (error) {
                let message = t('resetPassword.request.genericError');
                if (error instanceof HTTPError) {
                    try {
                        const data = await error.response.json();
                        if (data?.code === 'ACTIVATION_REQUIRED') {
                            // Invited accounts set their first password through activation.
                            const state: ActivateAccountState = {
                                identifier: rawIdentifier,
                            };
                            history.push('/activate', state);
                            return;
                        }
                        if (data?.message && typeof data.message === 'string') {
                            message = data.message;
                        }
                    } catch (parseError) {
                        message = t('resetPassword.request.genericError');
                    }
                } else if (error instanceof Error) {
                    message = error.message;
                }
                notify(message, true);
            } finally {
                setIsRequesting(false);
            }
        },
        [history, notify, t],
    );

    const handleResetPassword = useCallback(
        async (values: CompleteResetSchema) => {
            if (!codeSentTo) {
                notify(t('resetPassword.reset.genericError'), true);
                return;
            }

            if (!resetRequest) {
                notify(t('resetPassword.reset.genericError'), true);
                return;
            }

            if (values.newPassword !== values.confirmPassword) {
                notify(t('validation.passwordsMismatch'), true);
                return;
            }

            setIsResetting(true);
            const contactPayload =
                'email' in resetRequest
                    ? { email: resetRequest.email }
                    : { phoneNumber: resetRequest.phoneNumber };

            const payload = {
                ...contactPayload,
                code: values.code.trim(),
                newPassword: values.newPassword,
            };

            try {
                await ky.post('auth/reset-password', {
                    prefixUrl: SERVER_URL,
                    json: payload,
                });
                notify(t('resetPassword.reset.success'));
                history.push('/login');
            } catch (error) {
                let message = t('resetPassword.reset.genericError');
                if (error instanceof HTTPError) {
                    try {
                        const data = await error.response.json();
                        if (data?.message && typeof data.message === 'string') {
                            message = data.message;
                        }
                    } catch (parseError) {
                        message = t('resetPassword.reset.genericError');
                    }
                } else if (error instanceof Error) {
                    message = error.message;
                }
                notify(message, true);
            } finally {
                setIsResetting(false);
            }
        },
        [codeSentTo, history, notify, resetRequest, t],
    );

    const handleUseDifferentEmail = useCallback(() => {
        setCodeSentTo(undefined);
        setResetRequest(undefined);
    }, []);

    return (
        <div className="bg-fixed min-h-screen flex flex-col">
            <LandingPageHeader
                colors={headerColors}
                scrollItems={false}
                navItems={[{ id: Screen.Mundu, name: t('nav.home') }]}
                languageSelect
            />
            <div className="grow flex justify-center items-center px-6 py-10">
                <div className="flex flex-col gap-6 max-w-lg w-full">
                    <div className="flex flex-col gap-2 text-center">
                        <h1 className="font-bold text-2xl text-woodyBrown">
                            {t('resetPassword.title')}
                        </h1>
                        <p className="text-mongooseGray text-base">
                            {t('resetPassword.description')}
                        </p>
                    </div>
                    <div className="grid gap-6">
                        {!codeSentTo ? (
                            <div className="p-6 rounded-2xl bg-starkWhite/80 shadow-sm flex flex-col gap-4">
                                <div>
                                    <div className="font-semibold text-woodyBrown">
                                        {t('resetPassword.request.heading')}
                                    </div>
                                    <p className="text-sm text-mongooseGray">
                                        {t('resetPassword.instructions')}
                                    </p>
                                </div>
                                <Form
                                    schema={RequestCodeSchema}
                                    onSubmit={handleRequestCode}
                                    mode="onChange"
                                    className="flex flex-col gap-4"
                                >
                                    {({
                                        register,
                                        formState: { errors, isValid },
                                    }) => (
                                        <>
                                            <Input
                                                className="text-input"
                                                label={t(
                                                    'resetPassword.request.emailLabel',
                                                )}
                                                type="text"
                                                error={errors.identifier}
                                                disabled={isRequesting}
                                                autoComplete="username"
                                                {...register('identifier')}
                                            />
                                            <Button
                                                submit
                                                disabled={
                                                    !isValid || isRequesting
                                                }
                                                title={
                                                    isRequesting
                                                        ? t('login.submitting')
                                                        : t(
                                                              'resetPassword.request.submit',
                                                          )
                                                }
                                            />
                                        </>
                                    )}
                                </Form>
                            </div>
                        ) : (
                            <div className="p-6 rounded-2xl bg-starkWhite/80 shadow-sm flex flex-col gap-4">
                                <div>
                                    <div className="font-semibold text-woodyBrown">
                                        {t('resetPassword.reset.heading')}
                                    </div>
                                    <p className="text-sm text-mongooseGray">
                                        {t('resetPassword.instructions')}
                                    </p>
                                    <p className="text-xs text-mongooseGray mt-1">
                                        {t('resetPassword.request.success', {
                                            email: codeSentTo,
                                        })}
                                    </p>
                                </div>
                                <Form
                                    key={codeSentTo}
                                    schema={CompleteResetSchema}
                                    onSubmit={handleResetPassword}
                                    mode="onChange"
                                    className="flex flex-col gap-4"
                                >
                                    {({
                                        register,
                                        formState: { errors, isValid },
                                    }) => (
                                        <>
                                            <Input
                                                className="text-input"
                                                label={t(
                                                    'resetPassword.reset.codeLabel',
                                                )}
                                                error={errors.code}
                                                disabled={isResetting}
                                                autoComplete="one-time-code"
                                                {...register('code')}
                                            />
                                            <PasswordInput
                                                className="text-input"
                                                label={t(
                                                    'resetPassword.reset.newPasswordLabel',
                                                )}
                                                error={errors.newPassword}
                                                disabled={isResetting}
                                                autoComplete="new-password"
                                                {...register('newPassword')}
                                            />
                                            <PasswordInput
                                                className="text-input"
                                                label={t(
                                                    'resetPassword.reset.confirmPasswordLabel',
                                                )}
                                                disabled={isResetting}
                                                error={errors.confirmPassword}
                                                autoComplete="new-password"
                                                {...register('confirmPassword')}
                                            />
                                            <Button
                                                submit
                                                disabled={
                                                    !isValid || isResetting
                                                }
                                                title={
                                                    isResetting
                                                        ? t('login.submitting')
                                                        : t(
                                                              'resetPassword.reset.submit',
                                                          )
                                                }
                                            />
                                        </>
                                    )}
                                </Form>
                                <button
                                    type="button"
                                    className="text-sm text-woodyBrown underline underline-offset-2 self-start"
                                    onClick={handleUseDifferentEmail}
                                >
                                    {t('resetPassword.reset.useDifferentEmail')}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="text-center">
                        <Link
                            to="/login"
                            className="text-sm text-woodyBrown underline underline-offset-2"
                        >
                            {t('resetPassword.backToLogin')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};
